import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { viewAsProId } from './viewAs';
import { isCell } from './cell/config';
import { getCellSession, onCellSession, signOutCell, type CellSession } from './cell/cognito';
import { cellGet } from './cell/api';

/**
 * VIEW-AS IDENTITY (PRO-VIEW-1). While an operator is viewing a pro, useSession presents
 * the PRO's auth identity so every page resolves the pro through its normal reads — while
 * the underlying token stays the OPERATOR's own, so RLS itself is the validator: the mode
 * engages only if the operator's token can read that pro's row (authed_read_pros), and no
 * read returns anything the operator couldn't already see. Writes are refused at the
 * client's one door (supabase.ts) and structurally server-side.
 *
 * Resolved once per page load and cached module-level — every useSession() instance
 * shares one lookup. A pro who has never signed in has no auth_user_id and cannot be
 * previewed; the banner says so honestly.
 */
export type ViewAsState = {
  active: boolean;
  name: string | null;
  failed: string | null;
};
let viewAsCache: { authId: string; email: string | null; name: string } | 'failed:unreadable' | 'failed:unlinked' | null = null;
let viewAsPromise: Promise<void> | null = null;
const viewAsListeners = new Set<() => void>();

function resolveViewAs(): Promise<void> {
  if (viewAsPromise) return viewAsPromise;
  viewAsPromise = (async () => {
    const id = viewAsProId();
    if (!id) return;
    // CELL (M28): GET /v1/pro-hub/pros/{proId} — the same read, the viewer's own row-level security the check.
    const { data } = isCell()
      ? { data: (await cellGet<{ pro: { id: string; auth_user_id: string | null; email: string | null; first_name: string | null; last_name: string | null } | null }>(`/v1/pro-hub/pros/${id}`)).data?.pro ?? null }
      : await supabase
        .from('pros')
        .select('id, auth_user_id, email, first_name, last_name')
        .eq('id', id)
        .maybeSingle();
    if (!data) {
      // The operator's own token cannot read this pro — not their studio. RLS was the check.
      viewAsCache = 'failed:unreadable';
    } else if (!data.auth_user_id) {
      viewAsCache = 'failed:unlinked';
    } else {
      viewAsCache = {
        authId: data.auth_user_id as string,
        email: (data.email as string | null) ?? null,
        name: [data.first_name, data.last_name].filter(Boolean).join(' ') || 'this pro',
      };
      // the audit line — every view-as session is on the record. CELL (M28): log_pro_view writes a brain table that is
      // not on the cell (cell baseline REPORT: "function over a retired/brain table"); its home is the audit ledger
      // (M09) — recorded as an M28 finding, not silently dropped.
      if (!isCell()) void supabase.rpc('log_pro_view', { p_pro: id });
    }
    viewAsListeners.forEach((fn) => fn());
  })();
  return viewAsPromise;
}

export function useViewAs(): ViewAsState {
  const [, force] = useState(0);
  useEffect(() => {
    const bump = () => force((n) => n + 1);
    viewAsListeners.add(bump);
    if (viewAsProId()) void resolveViewAs();
    return () => { viewAsListeners.delete(bump); };
  }, []);
  const id = viewAsProId();
  if (!id) return { active: false, name: null, failed: null };
  if (viewAsCache === 'failed:unreadable') {
    return { active: false, name: null, failed: 'That pro is not at a studio this account can see.' };
  }
  if (viewAsCache === 'failed:unlinked') {
    return { active: false, name: null, failed: 'This pro has not signed in yet, so their hub cannot be previewed.' };
  }
  if (viewAsCache) return { active: true, name: viewAsCache.name, failed: null };
  return { active: false, name: null, failed: null }; // still resolving
}

/**
 * Subscribe to Supabase auth state. Returns `{ session, loading }`.
 * `loading` flips to false after the first session check completes —
 * before that, route guards should render a neutral splash so we
 * don't flash a login screen for users who already have a session.
 */
export function useSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    if (isCell()) {
      // CELL (M28): Tenean's Cognito session, presented in the shape every page already reads.
      const apply = async (c: CellSession | null) => { const s = c ? await sessionFromCell(c) : null; if (mounted) { setSession(s); setLoading(false); } };
      void getCellSession().then(apply);
      const off = onCellSession((c) => { void apply(c); });
      return () => { mounted = false; off(); };
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    // (CELL (M28): not reached on the cell build — the Cognito branch returned above.)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // VIEW-AS: present the pro's identity over the operator's own session.
  const [, force] = useState(0);
  useEffect(() => {
    const bump = () => force((n) => n + 1);
    viewAsListeners.add(bump);
    return () => { viewAsListeners.delete(bump); };
  }, []);
  const viewId = viewAsProId();
  if (viewId && session) {
    if (typeof viewAsCache === 'object' && viewAsCache !== null) {
      return {
        session: {
          ...session,
          user: { ...session.user, id: viewAsCache.authId, email: viewAsCache.email ?? undefined },
        } as Session,
        loading: false,
      };
    }
    if (viewAsCache === null) {
      void resolveViewAs();
      return { session: null, loading: true }; // gates splash while the mode resolves
    }
    // failed: fall through to the real session; the banner explains why
  }

  return { session, loading };
}

/**
 * Send a magic-link to `email`. Supabase emails the Pro a one-click
 * link that, when followed, redirects back to /auth/callback with a
 * PKCE `code` param that the SDK exchanges for a session.
 *
 * Requires:
 *   - Supabase Auth → Email provider enabled (default on)
 *   - This site's origin in the Supabase redirect allow-list. `emailRedirectTo`
 *     is built from window.location.origin, so a host that is not on that list
 *     bounces every pro to the Site URL instead of back here — the one silent
 *     way this whole sign-in breaks.
 *
 * VERIFIED 2026-07-30 against the pknt project's live auth config, because the
 * line this replaces still named `https://pkntco.com`, an address that has been
 * dead for weeks — and a stale note about the one setting that can silently
 * break sign-in is worse than no note:
 *     site_url        https://pkntops.com
 *     allow-list      https://pkntops.com/*  ·  https://hub.pkntops.com/**
 *                     https://imageos.co/**  ·  https://support.pkntops.com/**
 *                     https://dev.pkntops.com/**  ·  http://localhost:5173/**
 *                     plus the two workers.dev preview hosts
 * Serving this app from any other origin means adding it there first.
 */
export async function sendMagicLink(email: string): Promise<{ error?: string }> {
  // CELL (M28): email codes arrive with the email rail (M18); the cell build signs in with a password (Login.tsx).
  if (isCell()) return { error: 'Email sign-in links are not on the AWS staging hub yet — sign in with your password.' };
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      shouldCreateUser: true, // auto-create user on first sign-in
    },
  });
  if (error) return { error: error.message };
  return {};
}

/**
 * SIGN-OUT IS LOCAL TO THIS DEVICE, NEVER GLOBAL (Peter, 2026-09-11, decision d0aaa17a).
 * supabase-js's signOut() defaults to scope 'global', which revokes EVERY session the person holds
 * on every device. On 2026-09-11 one such call from the Owner Hub killed the session on Peter's
 * phone for half an hour. A pro who signs out here to sign in as someone else keeps their phone
 * exactly as it was. Guarded in auth.test.ts.
 */
export async function signOut(): Promise<void> {
  if (isCell()) { await signOutCell(); return; } // this device only, as below
  await supabase.auth.signOut({ scope: 'local' });
}

/**
 * Hook: returns the operator profile for the current user, or null if
 * the user isn't an operator. Used to gate admin routes (`/admin/*`).
 *
 * `loading` flips false after the lookup completes; until then, gates
 * should render a neutral splash so we don't flash a 404 for legitimate
 * operators on a slow connection.
 *
 * Reads from `operator_users` table — the same table the iOS app uses.
 * RLS allows authenticated users to read their own row by `auth_user_id`.
 */
export interface OperatorProfile {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  location_access: string[];
}

export function useOperator(): { operator: OperatorProfile | null; loading: boolean } {
  const { session, loading: sessionLoading } = useSession();
  const [operator, setOperator] = useState<OperatorProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionLoading) return;
    // CELL (M28): the operator tools (/admin/*) are not on the AWS Pro Hub; they belong to the Owner Hub there.
    if (isCell()) { setOperator(null); setLoading(false); return; }
    const userId = session?.user?.id;
    const email = session?.user?.email?.toLowerCase().trim();
    if (!userId && !email) { setOperator(null); setLoading(false); return; }

    let cancelled = false;
    (async () => {
      // 1) Preferred: lookup by auth_user_id (set after first sign-in via
      //    the operator_link helper, or backfilled manually).
      let row: OperatorProfile | null = null;
      // CELL (M28): not reached on the cell build (returned above); the three operator_users reads below are production only.
      if (userId) {
        const { data } = await supabase
          .from('operator_users')
          .select('id,email,display_name,role,location_access,auth_user_id')
          .eq('auth_user_id', userId)
          .maybeSingle();
        row = (data as OperatorProfile | null) ?? null;
      }

      // 2) Fallback: lookup by email. Lets newly-added operator_users rows
      //    work on their first login even before a backfill links
      //    auth_user_id. RLS on operator_users still requires the row's
      //    auth_user_id to match the session, so this fallback only
      //    succeeds when the user has linked themselves OR the row is
      //    readable by another route (corporate_admin). For the common
      //    case ("seeded an operator row by email; user just logged in
      //    for the first time"), we self-heal by writing auth_user_id
      //    back so subsequent lookups use the fast path.
      if (!row && email) { // CELL (M28): production only (see above)
        const { data } = await supabase
          .from('operator_users')
          .select('id,email,display_name,role,location_access,auth_user_id')
          .eq('email', email)
          .maybeSingle();
        row = (data as OperatorProfile | null) ?? null;
        if (row && userId && !(row as { auth_user_id?: string | null }).auth_user_id) {
          // self-heal: link this auth user to the operator row — CELL (M28): production only
          await supabase
            .from('operator_users')
            .update({ auth_user_id: userId })
            .eq('id', row.id);
        }
      }

      if (cancelled) return;
      setOperator(row);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session?.user?.id, session?.user?.email, sessionLoading]);

  return { operator, loading };
}

/**
 * CELL (M28): a Cognito session in the Supabase Session shape the pages read — user.id is the person's Tenean user id
 * (the same id auth.users had), access_token is the ID token the API checks, and user_metadata carries the terms
 * acceptance (stored in the cell, app.user_terms, read through GET /v1/pro-hub/terms), so ConsentGate reads it unchanged.
 */
export async function sessionFromCell(c: CellSession): Promise<Session> {
  const t = await cellGet<{ version: string | null; accepted_at: string | null }>('/v1/pro-hub/terms');
  return {
    access_token: c.idToken, refresh_token: '', token_type: 'bearer', expires_at: c.expiresAt, expires_in: Math.max(0, c.expiresAt - Math.floor(Date.now() / 1000)),
    user: {
      id: c.userId, email: c.email ?? undefined, aud: 'authenticated', app_metadata: {}, created_at: '',
      user_metadata: t.data?.version ? { tos_accepted_version: t.data.version, tos_accepted_at: t.data.accepted_at } : {},
    },
  } as Session;
}
